import React, { useState } from 'react';
import { DollarSign, Users, FileText, TrendingUp, Shield, Globe, Award, Target, CheckCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import RegistrationModal from '../../components/RegistrationModal';
import SEO from '../../components/SEO';
import ServicePricing from '../../components/ServicePricing';
import CTASection from '../../components/CTASection';

const CopyrightLicensingPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [modalMode, setModalMode] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const openModal = (mode) => {
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setModalMode(null);
  };

  const benefits = [
    {
      icon: DollarSign,
      title: "Revenue Generation",
      description: "Create passive income streams through royalties, licensing fees, and usage rights agreements."
    },
    {
      icon: Globe,
      title: "Market Expansion",
      description: "Reach new markets and audiences by licensing your works to qualified partners worldwide."
    },
    {
      icon: Shield,
      title: "Risk Management",
      description: "Protect your rights with professionally drafted agreements that clearly define usage terms and restrictions."
    },
    {
      icon: Target,
      title: "Strategic Partnerships",
      description: "Build profitable relationships with publishers, distributors, and content platforms."
    },
    {
      icon: Award,
      title: "Brand Control",
      description: "Maintain quality standards and brand integrity through carefully structured licensing terms."
    },
    {
      icon: TrendingUp,
      title: "Asset Monetization",
      description: "Maximize the value of your creative works by licensing multiple rights to different parties."
    }
  ];

  const licenseTypes = [
    {
      title: "Exclusive Licenses",
      description: "Grant one licensee exclusive rights to use your work in specific ways or territories.",
      features: ["Higher royalty rates", "Single licensee per territory", "Greater licensee commitment", "Stronger partnership"]
    },
    {
      title: "Non-Exclusive Licenses",
      description: "Allow multiple licensees to use your work simultaneously for maximum distribution.",
      features: ["Multiple revenue streams", "Broader market reach", "Greater flexibility", "Easier negotiations"]
    },
    {
      title: "Sublicensing Rights",
      description: "Permit licensees to grant sublicenses to third parties, expanding your work's reach.",
      features: ["Extended distribution", "Additional royalty tiers", "Rapid market penetration", "Reduced management"]
    },
    {
      title: "Merchandise Rights",
      description: "License your creative works for use on products, merchandise, and commercial goods.",
      features: ["Product royalties", "Brand extensions", "Consumer reach", "Cross-promotion"]
    }
  ];

  const process = [
    {
      step: "1",
      title: "Consultation & Valuation",
      description: "We evaluate your intellectual property portfolio, determine market value, and identify optimal licensing opportunities.",
      timeline: "3-5 days"
    },
    {
      step: "2",
      title: "Agreement Drafting",
      description: "Our attorneys draft comprehensive licensing agreements with precise terms, royalty structures, and protection clauses.",
      timeline: "5-7 days"
    },
    {
      step: "3",
      title: "Negotiation & Management",
      description: "We handle negotiations with potential licensees, finalize terms, and provide ongoing license management and enforcement.",
      timeline: "Ongoing"
    }
  ];

  const packages = [
    {
      name: "Basic License",
      price: "$799",
      description: "Single license agreement",
      features: [
        "1 license agreement draft",
        "Basic negotiation support",
        "Terms consultation",
        "Agreement review",
        "Email support",
        "Standard delivery"
      ],
      popular: false
    },
    {
      name: "Professional License",
      price: "$1,499",
      description: "Comprehensive licensing",
      features: [
        "Up to 3 license agreements",
        "Full negotiation support",
        "Royalty structure design",
        "Licensee vetting",
        "6-month management",
        "Priority support",
        "Custom terms"
      ],
      popular: true
    },
    {
      name: "Enterprise License",
      price: "$3,499",
      description: "Full licensing program",
      features: [
        "Unlimited agreements",
        "Portfolio licensing strategy",
        "International licensing",
        "Ongoing management",
        "Royalty audits",
        "Dedicated licensing team",
        "24/7 support"
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      question: "What is copyright licensing and how does it work?",
      answer: "Copyright licensing is the process of granting permission to others to use your copyrighted works in exchange for payment. You retain ownership while allowing licensees to use, reproduce, distribute, or display your work according to agreed terms. Licensing can be exclusive or non-exclusive, limited by territory, duration, or usage type. It's a powerful way to monetize your creative works while maintaining control over how they're used."
    },
    {
      question: "How much money can I make from licensing my copyrights?",
      answer: "Licensing revenue varies widely based on the work's type, market demand, exclusivity, and usage scope. Typical royalty rates range from 5-15% for books, 8-20% for music, and 10-25% for visual art. Exclusive licenses command higher rates than non-exclusive. Major works can generate six to seven figures annually, while niche works might earn thousands. We help you structure deals that maximize your earning potential based on market conditions and your work's value."
    },
    {
      question: "What's the difference between exclusive and non-exclusive licenses?",
      answer: "An exclusive license grants one licensee sole rights to use your work in specified ways—you cannot license the same rights to anyone else, including yourself in some cases. Non-exclusive licenses allow multiple licensees to use the work simultaneously. Exclusive licenses typically command higher fees but limit your ability to monetize broadly. Non-exclusive licenses generate multiple revenue streams but at lower individual rates. The choice depends on your monetization strategy and market opportunities."
    },
    {
      question: "How long should a copyright license last?",
      answer: "License duration varies by industry and usage type. Common terms range from 1-5 years for commercial licenses, with options to renew. Shorter terms (1-2 years) provide flexibility to renegotiate as your work gains value. Longer terms (3-5+ years) offer stability and often command higher upfront fees. Some licenses are granted in perpetuity (forever) for substantial one-time payments. We help you choose durations that balance immediate revenue with long-term value."
    },
    {
      question: "Do I need to register my copyright before licensing it?",
      answer: "While registration isn't legally required to license your work, it's highly recommended. Registration strengthens your negotiating position, makes licensees more confident in the deal, and provides critical legal benefits if disputes arise. Registered copyrights can pursue statutory damages and attorney's fees in infringement cases, making your licenses more enforceable. Most professional licensees prefer or require registration before entering agreements."
    },
    {
      question: "How are royalties calculated and paid?",
      answer: "Royalties are typically calculated as a percentage of revenue (gross or net) or as fixed per-unit fees. Common structures include: (1) percentage of sales revenue (5-20%), (2) per-copy or per-download fees ($0.10-$5.00), or (3) minimum guarantees plus overages. Payments are usually quarterly or semi-annually, with detailed accounting reports. Agreements should specify the calculation method, payment schedule, audit rights, and late payment penalties. We draft clear royalty terms to avoid disputes."
    },
    {
      question: "Can I license my work internationally?",
      answer: "Yes, copyright licensing can be structured for international markets. You can grant worldwide rights or limit licenses to specific countries or regions. International licensing often involves currency considerations, foreign tax implications, and varying copyright laws. Territory-specific licenses allow you to optimize pricing for different markets and work with local partners who understand regional audiences. We help structure international agreements that comply with relevant laws and maximize global revenue."
    },
    {
      question: "What happens if a licensee violates the license terms?",
      answer: "License violations are breaches of contract. Remedies depend on your agreement terms but typically include: (1) written notice and cure period, (2) termination of license, (3) damages for unauthorized use, and (4) injunctions to stop further violations. Well-drafted agreements specify violation consequences, including liquidated damages. In serious cases, violations may constitute copyright infringement, allowing you to pursue statutory damages. We draft agreements with strong enforcement provisions and help resolve disputes efficiently."
    },
    {
      question: "Should I use a licensing agent or manage licenses myself?",
      answer: "Licensing agents (taking 10-25% commission) handle negotiations, collect royalties, and manage licensee relationships—worthwhile for high-value portfolios or if you lack time/expertise. Self-management gives you direct control and avoids commissions but requires business acumen and time investment. Many creators start self-managing simple licenses and engage agents as their portfolios grow. We can draft agreements for either approach and provide management services if you prefer professional oversight without full agent representation."
    },
    {
      question: "How do I find potential licensees for my work?",
      answer: "Finding licensees requires market research and networking. Strategies include: (1) attending industry trade shows and conferences, (2) joining professional organizations and licensing associations, (3) using licensing marketplaces and platforms, (4) directly contacting potential users of your work, (5) working with licensing agents who have established networks, and (6) leveraging online presence and portfolio websites. We can help identify potential licensees, make introductions through our network, and develop marketing materials that showcase your work's licensing potential."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Copyright Licensing Services - Monetize Your Creative Works"
        description="Generate revenue from your creative works with expert licensing agreements, negotiation, and management."
        keywords="copyright licensing, royalty agreements, creative work monetization, licensing contracts, copyright revenue"
      />

      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
            <img src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=900w=1600&q=80q=50" alt="" loading="eager" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/90 via-[#1a0f5e]/85 to-[#0d0830]/90"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF035B]/5 rounded-full blur-2xl"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FF035B]/20 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <DollarSign className="w-4 h-4" />
                <span>Monetize Your Creative Works</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Copyright Licensing Services to Generate Passive Income
              </h1>
              <p className="text-xl mb-8 text-gray-300">
                Turn your creative works into revenue streams. Expert licensing agreements, royalty negotiations, and ongoing management to maximize your copyright value.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => openModal('registration')}
                  className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white px-8 py-4 rounded-lg font-semibold transition-all hover:shadow-xl hover:scale-105"
                >
                  Start Licensing
                </button>
                <button 
                  onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
                  className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all"
                >
                  Speak with Expert
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Expert Negotiation</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Ongoing Management</span>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <h3 className="text-lg font-bold mb-6">Licensing Benefits</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Passive Income</strong>
                    <span className="text-gray-300">Earn royalties from your work without giving up ownership</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Market Expansion</strong>
                    <span className="text-gray-300">Reach new audiences through strategic licensing partnerships</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Professional Terms</strong>
                    <span className="text-gray-300">Attorney-drafted agreements that protect your rights and maximize value</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why License Your Copyrights?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strategic licensing transforms creative works into profitable assets
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="group bg-white hover:bg-gradient-to-br hover:from-[#FF035B] hover:to-[#2F1A8E] rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 cursor-pointer hover:shadow-xl transition-all border border-gray-100">
                <benefit.icon className="w-12 h-12 text-[#FF035B] group-hover:text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500 leading-relaxed group-hover:text-white/90 transition-colors duration-500">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* License Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Types of Copyright Licenses
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the licensing structure that fits your goals
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {licenseTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all">
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-3">{type.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500 mb-6">{type.description}</p>
                <div className="space-y-2">
                  {type.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Licensing Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From strategy to execution—we handle every step
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] text-white rounded-xl p-6 shadow-lg">
                  <div className="text-4xl font-bold mb-2 opacity-50">{item.step}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-sm mb-4 text-white/90">{item.description}</p>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Clock className="w-4 h-4" />
                    {item.timeline}
                  </div>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-[#FF035B] to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServicePricing showPrices={false} title="Copyright Licensing Services" subtitle="Get a personalized quote for your specific needs" />


      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about copyright licensing
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
                >
                  <span className="font-semibold text-gray-900 pr-8">{faq.question}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-[#FF035B] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Monetize Your Creative Works?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Start generating passive income from your copyrights. Our licensing experts will help you structure profitable deals that protect your rights.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button 
              onClick={() => openModal('registration')}
              className="bg-white text-[#FF035B] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:shadow-xl"
            >
              Start Licensing Today
            </button>
            <button 
              onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#FF035B] transition-all"
            >
              Schedule Consultation
            </button>
          </div>
        </div>
      </section>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType="registration"
      />
    </div>
  );
};

export default CopyrightLicensingPage;